import type { IOptions } from "@fluidattacks/design";
import {
  type IFilterOptions,
  type IOptionsProps,
  type IUseFilterProps,
  useFilters,
} from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IToEInputFilters } from "./types";

import type { IToeInputData } from "pages/group/surface/inputs/types";
import { useFiltersStorage } from "storage/filters";
import {
  getCheckedFilter,
  getCheckedFilters,
  getDateRangeFilter,
  getNewFilter,
} from "utils/filters";
import { formatBoolean, formatStatus } from "utils/format-helpers";

const useToEInputsFilters = ({
  groupName,
  setBackFilters,
  setFilteredDataset,
  toeInputs,
  toeInputsComponents,
}: IToEInputFilters): IUseFilterProps<IToeInputData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const componentOptions = useMemo((): IOptions[] => {
    return Array.from(
      new Map(
        toeInputsComponents.map((component): [string, IOptions] => [
          component,
          { label: component, value: component },
        ]),
      ).values(),
    );
  }, [toeInputsComponents]);

  const options: IOptionsProps<IToeInputData>[] = useMemo(
    (): IOptionsProps<IToeInputData>[] => [
      {
        filterOptions: [
          {
            key: "rootNickname",
            label: t("group.toe.inputs.root"),
            type: "text",
          },
        ],
        label: t("group.toe.inputs.root"),
      },
      {
        filterOptions: [
          {
            key: "component",
            label: t("group.toe.inputs.component"),
            options: componentOptions,
            type: "checkboxes",
          },
        ],
        label: t("group.toe.inputs.component"),
      },
      {
        filterOptions: [
          {
            key: "entryPoint",
            label: t("group.toe.inputs.entryPoint"),
            type: "text",
          },
        ],
        label: t("group.toe.inputs.entryPoint"),
      },
      {
        filterOptions: [
          {
            key: "hasVulnerabilities",
            label: t("group.toe.inputs.status"),
            options: [
              { label: formatStatus(true), value: "true" },
              { label: formatStatus(false), value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.toe.inputs.status"),
      },
      {
        filterOptions: [
          {
            key: "seenAt",
            label: t("group.toe.inputs.seenAt"),
            type: "dateRange",
          },
        ],
        label: t("group.toe.inputs.seenAt"),
      },
      {
        filterOptions: [
          {
            key: "bePresent",
            label: t("group.toe.inputs.bePresent"),
            options: [
              { label: formatBoolean(true), value: "true" },
              { label: formatBoolean(false), value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.toe.inputs.bePresent"),
      },
    ],
    [componentOptions, t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("toe_inputs", groupName) as string | null;
  }, [getValues, groupName]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("toe_inputs", value, groupName);
    },
    [setValues, groupName],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IToeInputData>[]): void => {
      const newFiltersArray = newFilters as IFilterOptions<IToeInputData>[];
      const components = getCheckedFilters(newFiltersArray, "component");
      const rootNickname = getNewFilter(newFiltersArray, "rootNickname")?.value;
      const bePresent = getCheckedFilter(newFiltersArray, "bePresent")?.value;
      const entryPoint = getNewFilter(newFiltersArray, "entryPoint")?.value;
      const hasVulnerabilities = getCheckedFilter(
        newFiltersArray,
        "hasVulnerabilities",
      )?.value;
      const { from: fromSeenAt, to: toSeenAt } = getDateRangeFilter(
        newFiltersArray,
        "seenAt",
      );

      const toeInputsBackFilters = {
        bePresent: bePresent === undefined ? undefined : bePresent === "true",
        components: components ?? undefined,
        entryPoint: entryPoint ?? undefined,
        fromSeenAt,
        hasVulnerabilities:
          hasVulnerabilities === undefined
            ? undefined
            : hasVulnerabilities === "true",
        rootNickname,
        toSeenAt,
      };
      setBackFilters(toeInputsBackFilters);
    },
    [setBackFilters],
  );

  return useFilters({
    dataset: toeInputs,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useToEInputsFilters };
